import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  cloudListBillingPlans,
  cloudGetBillingSubscription,
  cloudGetBillingUsage,
  cloudUpdateBillingSubscription,
  getCloudToken,
} from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { planCatalog, planFromApi, type PlanId } from "./plan";

export type BillingSubscription = {
  plan: string;
  label: string;
  status: string;
  maxProjects: number;
  maxDevices: number;
};

export type BillingUsage = {
  projects: { current: number; limit: number; percentage: number };
  devices: { current: number; limit: number; percentage: number };
};

type BillingState = {
  ready: boolean;
  syncing: boolean;
  subscription: BillingSubscription | null;
  usage: BillingUsage | null;
  plans: Array<{ id: string; label: string; maxProjects: number; maxDevices: number }>;
  plan: (typeof planCatalog)[PlanId];
  projectQuota: number;
  deviceQuota: number;
  projectUsed: number;
  deviceUsed: number;
  canCreateProject: boolean;
  refresh: () => Promise<void>;
  upgradePlan: (planId: string) => Promise<void>;
};

const BillingContext = createContext<BillingState | null>(null);

export function BillingProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [ready, setReady] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [subscription, setSubscription] = useState<BillingSubscription | null>(null);
  const [usage, setUsage] = useState<BillingUsage | null>(null);
  const [plans, setPlans] = useState<
    Array<{ id: string; label: string; maxProjects: number; maxDevices: number }>
  >([]);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || !getCloudToken()) {
      setReady(false);
      return;
    }
    setSyncing(true);
    try {
      const [sub, use, listed] = await Promise.all([
        cloudGetBillingSubscription(),
        cloudGetBillingUsage(),
        cloudListBillingPlans(),
      ]);
      setSubscription(sub);
      setUsage(use);
      setPlans(listed);
      setReady(true);
    } catch {
      setReady(false);
    } finally {
      setSyncing(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const plan = useMemo(
    () => planFromApi(subscription?.plan),
    [subscription?.plan],
  );

  const projectQuota = usage?.projects.limit ?? subscription?.maxProjects ?? plan.projectQuota;
  const deviceQuota = usage?.devices.limit ?? subscription?.maxDevices ?? plan.deviceQuota;
  const projectUsed = usage?.projects.current ?? 0;
  const deviceUsed = usage?.devices.current ?? 0;

  const upgradePlan = useCallback(
    async (planId: string) => {
      await cloudUpdateBillingSubscription(planId);
      await refresh();
    },
    [refresh],
  );

  const value = useMemo<BillingState>(
    () => ({
      ready,
      syncing,
      subscription,
      usage,
      plans,
      plan,
      projectQuota,
      deviceQuota,
      projectUsed,
      deviceUsed,
      canCreateProject: projectUsed < projectQuota,
      refresh,
      upgradePlan,
    }),
    [
      ready,
      syncing,
      subscription,
      usage,
      plans,
      plan,
      projectQuota,
      deviceQuota,
      projectUsed,
      deviceUsed,
      refresh,
      upgradePlan,
    ],
  );

  return (
    <BillingContext.Provider value={value}>{children}</BillingContext.Provider>
  );
}

export function useBilling() {
  const ctx = useContext(BillingContext);
  if (!ctx) {
    throw new Error("useBilling must be used within BillingProvider");
  }
  return ctx;
}
